import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { brandsApi } from '../../../api/brands.js';
import { usePaginatedQuery } from '../../../hooks/usePaginatedQuery.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { DataTable } from '../../../components/DataTable.jsx';
import { Pagination } from '../../../components/ui/Pagination.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.jsx';
import { t } from '../../../lib/i18n.js';

import { BrandFormModal } from './BrandFormModal.jsx';

export function BrandsPage() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const canCreate = hasPermission('products:create');
  const canUpdate = hasPermission('products:update');
  const canDelete = hasPermission('products:delete');

  const { items, isLoading, pagination, setPage, filters, setFilters } = usePaginatedQuery({
    key: ['brands', 'list'],
    fetcher: brandsApi.list
  });

  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const deleteMutation = useMutation({
    mutationFn: (id) => brandsApi.remove(id),
    onSuccess: () => {
      toast.success('Brand eliminato.');
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      setConfirmTarget(null);
    },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const columns = [
    { key: 'name', header: 'Nome', render: (r) => <span className="text-ink">{r.name}</span> },
    { key: 'slug', header: 'Slug', render: (r) => <code className="rounded bg-border/40 px-1.5 py-0.5 text-xs">{r.slug}</code> },
    { key: 'website', header: 'Sito', render: (r) => r.website ? <a href={r.website} target="_blank" rel="noreferrer" className="text-accent hover:underline">{r.website.replace(/^https?:\/\//, '')}</a> : <span className="text-ink-faint">-</span> },
    { key: 'active', header: 'Stato', render: (r) => <Badge tone={r.active ? 'success' : 'neutral'}>{r.active ? 'Attivo' : 'Disattivo'}</Badge> },
    {
      key: 'actions', header: '', align: 'right',
      render: (r) => (
        <div className="flex justify-end gap-2">
          {canUpdate && <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => { setEditing(r); setFormOpen(true); }}>Modifica</button>}
          {canDelete && <button type="button" className="text-xs text-danger hover:underline" onClick={() => setConfirmTarget(r)}>Elimina</button>}
        </div>
      )
    }
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Brand"
        description="Marche gestite e associate ai prodotti."
        actions={canCreate ? <Button onClick={() => { setEditing(null); setFormOpen(true); }}>Nuovo brand</Button> : null}
      />

      <div className="flex flex-wrap items-end gap-3">
        <Input className="w-64" label="Cerca" placeholder="nome o slug..." value={filters.q || ''} onChange={(e) => setFilters({ q: e.target.value || undefined })} />
      </div>

      <DataTable columns={columns} rows={items} loading={isLoading} empty={{ title: 'Nessun brand', description: 'Crea il primo brand o esegui il seed dai prodotti esistenti.' }} />
      <Pagination pagination={pagination} onPageChange={setPage} />

      <BrandFormModal open={formOpen} onClose={() => { setFormOpen(false); setEditing(null); }} initial={editing} />

      <ConfirmDialog
        open={Boolean(confirmTarget)}
        title="Eliminare il brand?"
        description={confirmTarget ? `"${confirmTarget.name}" sarà rimosso. I prodotti collegati perderanno l'associazione (mantengono il nome).` : ''}
        destructive
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={() => deleteMutation.mutate(confirmTarget._id)}
      />
    </div>
  );
}
